import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type { ComponentTemplate } from "@v1/types/component-template";
import type { DropTarget as AlchemyDropTarget } from "@v1/types/drop-target";

import * as React from "react";

import useHandleReplaceComponentWithDesignLibraryReferences from "@v1/hooks/designLibrary/useHandleReplaceComponentWithDesignLibraryReferences";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { useStoreAndDraftElementProducts } from "@v1/hooks/useStoreProducts";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import useDragAndDrop from "@v1/providers/DragAndDropProvider";
import { selectLocaleData } from "@v1/reducers/commerce-reducer";
import {
  selectComponentDataMapping,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  selectGetAttribute,
} from "@v1/reducers/core-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import { finalizeDropComponentActions } from "@v1/utils/drop";
import getUpdatedImageComponentTemplate from "@v1/utils/getUpadatedImageComponentTemplate";

import { selectCanvasDeltaXY } from "./canvas-reducer";

export function useDropHandler({
  onDrop: onDropProp,
  componentIdToDrag,
  componentNodeToDrag,
}: {
  onDrop: () => void;
  componentIdToDrag: string | null;
  componentNodeToDrag: HTMLElement | null;
}) {
  const applyComponentAction = useApplyComponentAction();
  const setDraftElement = useSetDraftElement();
  const store = useEditorStore();
  const { onSuccessFinishDrop, onDrop: onDropRef } = useDragAndDrop();
  // Note (Fran, 2022-12-06): We need to grab all the store and draft element
  // products, because sometimes the cache of the fetch products are only a few
  // of them. And for drop component templates with specific products, we will
  // not replace them correctly.
  const { products } = useStoreAndDraftElementProducts();
  const templateProduct =
    useEditorSelector(selectTemplateEditorStoreProduct) ?? null;
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = useEditorSelector(selectLocaleData);

  const { deltaY } = useEditorSelector(selectCanvasDeltaXY);
  const errorToast = useV1ErrorToast();
  const { handleReplaceComponentWithDesignLibraryReferences } =
    useHandleReplaceComponentWithDesignLibraryReferences();
  const onDrop = React.useCallback(
    (
      target: AlchemyDropTarget,
      template: ComponentTemplate | null | undefined,
      previousActions: UseApplyComponentActionType[],
      isSavedComponent?: boolean,
    ) => {
      onDropProp();
      // NOTE (Reinaldo): If we are trying to drop an existing component into itself, don't do it.
      if (!template && target.componentId === componentIdToDrag) {
        return;
      }

      const draftElement =
        selectDraftElement_warningThisWillRerenderOnEveryUpdate(
          store.getState(),
        );

      if (!draftElement) {
        return;
      }

      if (!componentNodeToDrag) {
        if (target.error) {
          errorToast(
            "Failed Dropping Component",
            target.error,
            "error.component.drop",
            {
              error: target.error,
            },
          );
        }
        if (previousActions?.length > 0) {
          applyComponentAction({
            type: "applyCompositeAction",
            value: previousActions,
          });
        }
        return;
      }

      const updatedImageTemplate = getUpdatedImageComponentTemplate(
        template,
        deltaY,
      );

      const componentDataMapping = selectComponentDataMapping(store.getState());

      const result = finalizeDropComponentActions(
        target,
        updatedImageTemplate ?? template ?? null,
        draftElement!,
        componentIdToDrag!,
        selectGetAttribute(store.getState()),
        componentDataMapping,
        {
          products,
          currencyCode: activeCurrency,
          language: activeLanguage,
          moneyFormat,
          moneyWithCurrencyFormat,
          templateProduct,
          isEditor: true,
          isShopifyProductsLoading: false,
        },
        isSavedComponent,
        handleReplaceComponentWithDesignLibraryReferences,
      );

      if (result.result === "error") {
        errorToast(
          "Dragging not possible",
          result.message ?? "Unknown error",
          "error.component.drop",
          {
            error: result.message ?? "Unknown error",
          },
        );
        return;
      }
      const componentActions = (previousActions ?? []).concat(result.actions);
      if (componentActions.length > 0) {
        applyComponentAction({
          type: "applyCompositeAction",
          value: componentActions,
        });

        if (result.addedComponent) {
          setDraftElement({
            componentIds: [result.addedComponent.id],
          });

          onSuccessFinishDrop?.(result.addedComponent, result.parentId);
        }
      }
    },
    [
      store,
      applyComponentAction,
      onSuccessFinishDrop,
      setDraftElement,
      componentIdToDrag,
      componentNodeToDrag,
      deltaY,
      products,
      activeCurrency,
      activeLanguage,
      moneyFormat,
      moneyWithCurrencyFormat,
      onDropProp,
      templateProduct,
      errorToast,
      handleReplaceComponentWithDesignLibraryReferences,
    ],
  );

  React.useEffect(() => {
    if (onDropRef) {
      onDropRef.current = onDrop;
    }
  }, [onDrop, onDropRef]);

  return { onDrop };
}
